import { Button, DatePicker, Form, Input, InputNumber, Select } from "antd";
import { Fragment, type CSSProperties } from "react";
import type { SearchField } from "./types";

export interface SearchFormProps {
    fields: SearchField[];
    initialValues?: Record<string, unknown>;
    loading?: boolean;
    onSearch: (values: Record<string, unknown>) => void;
    onReset?: (values: Record<string, unknown>) => void;
}
/** 按旧版列配置生成查询表单，保留 Enter 查询与默认值重置行为。 */
type SearchGridStyle = CSSProperties & Record<`--search-${string}`, string | number>;

/** 将 Vue el-col 的 span/offset/断点参数转成 CSS 变量，由公共样式统一解析。 */
function getGridStyle(field: SearchField): SearchGridStyle {
    const style: SearchGridStyle = { "--search-span": field.span ?? 4 };
    if (field.xs != null) style["--search-span-xs"] = field.xs;
    if (field.sm != null) style["--search-span-sm"] = field.sm;
    if (field.md != null) style["--search-span-md"] = field.md;
    if (field.lg != null) style["--search-span-lg"] = field.lg;
    if (field.xl != null) style["--search-span-xl"] = field.xl;
    return style;
}

/** 按旧版列配置生成查询表单，保留 Enter 查询与默认值重置行为。 */
export function SearchForm({ fields, initialValues, loading, onSearch, onReset }: SearchFormProps) {
    const [form] = Form.useForm();
    const defaults = Object.fromEntries(
        fields.map(field => [field.key, field.defaultValue]).filter(([, value]) => value != null)
    );
    /** 重置后立即用默认参数重新查询，与 Vue useTable.reset 一致。 */
    const reset = () => {
        form.resetFields();
        const values = form.getFieldsValue();
        onReset?.(values);
        onSearch(values);
    };
    return (
        <Form
            className="common-search-form"
            form={form}
            initialValues={{ ...defaults, ...initialValues }}
            layout="inline"
            onFinish={onSearch}
        >
            {fields
                .filter(field => !field.hidden)
                .sort((left, right) => (left.order ?? 99) - (right.order ?? 99))
                .map(field => {
                    return (
                        <Fragment key={field.key}>
                            {field.offset ? <div className="common-search-offset" data-search-offset={field.key} style={{ "--search-offset": field.offset } as SearchGridStyle} /> : null}
                            <div
                                className={`common-search-field${field.type === "dateRange" ? " common-search-field--date-range" : ""}`}
                                data-search-key={field.key}
                                style={getGridStyle(field)}
                            >
                                <Form.Item
                                    name={field.key}
                                    label={field.label}
                                    rules={field.required ? [{ required: true, message: `请输入${field.label ?? "查询条件"}` }] : undefined}
                                >
                                    {field.type === "select" ? (
                                        <Select allowClear mode={field.multiple ? "multiple" : undefined} options={field.options} placeholder={field.placeholder ?? `请选择${field.label ?? ""}`} />
                                    ) : field.type === "number" ? (
                                        <InputNumber placeholder={field.placeholder ?? `请输入${field.label ?? ""}`} />
                                    ) : field.type === "dateRange" ? (
                                        <DatePicker.RangePicker
                                            placeholder={field.rangePlaceholders}
                                            separator={field.rangeSeparator ?? "至"}
                                        />
                                    ) : field.type === "date" ? (
                                        <DatePicker placeholder={field.placeholder ?? `请选择${field.label ?? ""}`} />
                                    ) : (
                                        <Input allowClear placeholder={field.placeholder ?? `请输入${field.label ?? ""}`} />
                                    )}
                                </Form.Item>
                            </div>
                        </Fragment>
                    );
                })}
            <Form.Item className="common-search-actions">
                <Button type="primary" htmlType="submit" loading={loading}>
                    查询
                </Button>
                {fields.length > 1 || fields[0]?.type !== "input" ? (
                    <Button onClick={reset} disabled={loading}>
                        重置
                    </Button>
                ) : null}
            </Form.Item>
        </Form>
    );
}
